import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import qs from 'query-string';

type SearchParamsType = {
    [key: string]: number | string | string[] | boolean | undefined;
};

const PARSE_OPTIONS = {
    parseBooleans: true,
    parseNumbers: true,
} as const;

const STRINGIFY_OPTIONS = {
    skipNull: false,
    skipEmptyString: false,
    encode: true,
} as const;

export interface SearchParamsWriteOptions {
    /** Push a history entry, so Back undoes the write. Default: replace. */
    push?: boolean;
    /** Tell the home page not to scroll back to the top for this write. */
    preserveScroll?: boolean;
}

export interface PreserveScrollState {
    preserveScroll?: boolean;
}

/**
 * A digit string past `Number.MAX_SAFE_INTEGER` stays text: as a number it
 * would lose its last digits, which a long proof-of-purchase code cannot.
 */
function parseSearchParams(search: string) {
    const typed = qs.parse(search, PARSE_OPTIONS);
    const raw = qs.parse(search);
    for (const [key, value] of Object.entries(typed)) {
        const written = raw[key];
        if (
            typeof value === 'number' &&
            !Number.isSafeInteger(value) &&
            typeof written === 'string' &&
            /^\d+$/.test(written)
        ) {
            typed[key] = written;
        }
    }
    return typed;
}

function stringifyParams(params: Record<string, unknown>): string {
    const cleaned = Object.fromEntries(
        Object.entries(params).filter(([, value]) => value !== undefined)
    );
    return qs.stringify(cleaned, STRINGIFY_OPTIONS);
}

// One instance per component, widening T to cover every param it owns: a
// second instance keeps its own mirror of the URL, and a write through it can
// clobber a write made through the first (see the test file for a repro).
export function useSearchParams<T extends SearchParamsType>() {
    const location = useLocation();
    const history = useHistory();
    const [params, setParams] = React.useState(() =>
        parseSearchParams(location.search)
    );
    const skipNextSyncRef = React.useRef(false);
    // Mirrors the params we last read or wrote. A write merges onto this
    // instead of onto the `params` state, so `setParamsValue` does not have to
    // close over `params` — that would hand it a new identity on every URL
    // change and re-run every consumer callback keyed on it.
    const paramsRef = React.useRef(params);

    // Sync state with URL changes (skip if we just manually updated)
    React.useEffect(() => {
        if (skipNextSyncRef.current) {
            skipNextSyncRef.current = false;
            return;
        }
        const next = parseSearchParams(location.search);
        paramsRef.current = next;
        setParams(next);
    }, [location.search]);

    // Update URL and state. The navigation and the skip flag deliberately sit
    // outside the state updater: React may call an updater more than once, and
    // an updater runs during render, so navigating in there replaced the URL
    // mid-render and could double-fire.
    const setParamsValue = React.useCallback(
        (newParams: Partial<T>, options: SearchParamsWriteOptions = {}) => {
            const merged = { ...paramsRef.current, ...newParams };
            const search = stringifyParams(merged);
            const newSearch = search ? `?${search}` : '';
            const next = parseSearchParams(newSearch);

            // Advance the mirror synchronously so two writes in the same tick
            // merge onto each other rather than the second clobbering the first.
            paramsRef.current = next;
            setParams(next);

            // Only claim the next sync when the URL actually moves. The sync
            // effect keys off `location.search`, so a flag raised for a no-op
            // write would never be consumed and would instead swallow a later
            // back/forward navigation.
            if (newSearch !== history.location.search) {
                skipNextSyncRef.current = true;
                const to = {
                    pathname: history.location.pathname,
                    search: newSearch,
                    state: options.preserveScroll
                        ? { preserveScroll: true }
                        : undefined,
                };
                if (options.push) {
                    history.push(to);
                } else {
                    history.replace(to);
                }
            }
        },
        [history]
    );

    // Convenience method: setParam('key', value)
    const setParam = React.useCallback(
        <K extends keyof T>(
            key: K,
            value: T[K],
            options?: SearchParamsWriteOptions
        ) => {
            setParamsValue(
                ({ [key]: value } as unknown) as Partial<T>,
                options
            );
        },
        [setParamsValue]
    );

    return {
        params: params as T,
        setParam,
        setParams: setParamsValue,
    };
}
